import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IoMdCheckmark } from 'react-icons/io';
import {
  DescriptionText,
  IconBackground,
  ModalButtonsContainer,
  PaymentModal,
  TitleText
} from '../../PaymentComponents';
import { StyledModalBody } from '../../../../components/Modals';
import { Flex } from '../../../../components/ui/Flex';
import { ButtonBodyVariant } from '../../../../components/ui/ButtonVariant';
import { closeActiveModal } from '../../../../helper/modals';
import { Palette } from '../../../../components/utils';
import { refundVopayPayment } from '../../../../redux/actions/payments';
import { dispatchPaymentsPageRefetch } from '../../PaymentUtils';
import { setToast } from '../../../../redux/actions/window';

function parsePaymentId(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

const RefundPaymentConfirmation = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { modal, data } = useSelector((state) => state.headerModal);

  const residentName =
    data?.payer_name != null && String(data.payer_name).trim() !== ''
      ? String(data.payer_name).trim()
      : '—';

  const refundAmount = data?.deposit_amount ?? data?.amount;
  const [isRefunding, setIsRefunding] = useState(false);

  const refundPayment = () => {
    if (isRefunding) return;

    const paymentId = parsePaymentId(data?.payment_id);
    const buildingId = Number(data?.building_id);
    if (!paymentId || !Number.isFinite(buildingId) || buildingId <= 0) {
      dispatch(
        setToast({
          toastShow: true,
          toastMessage: t('Could not refund payment: missing payment details'),
          modal: 'errorToast'
        })
      );
      return;
    }

    const ik =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    setIsRefunding(true);

    dispatch(
      refundVopayPayment({
        body: {
          BuildingID: buildingId,
          PaymentId: paymentId,
          IK: ik
        },
        success: () => {
          closeActiveModal();
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Payment refund has been successfully initiated')
            })
          );
          dispatchPaymentsPageRefetch();
        },
        fail: (err) => {
          setIsRefunding(false);
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Could not refund payment: {{error}}', {
                error: err?.data?.message || err?.data?.Message || t('Unknown error')
              }),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const handleClose = () => {
    if (isRefunding) return;
    closeActiveModal();
  };

  return (
    <PaymentModal
      show={modal === 'RefundPaymentConfirmation'}
      onClose={handleClose}
      closeOnBackdrop={false}
      width={'460px'}
      padding={'2.25rem 1.5rem 3rem 1.5rem'}>
      <StyledModalBody>
        <Flex direction="column" alignItems="center" gap="1.5rem">
          <IconBackground background={Palette.GREEN_500}>
            <IoMdCheckmark
              size={32}
              color={Palette.WHITE}
              style={{ position: 'absolute', right: '12px', top: '11px' }}
            />
          </IconBackground>
          <Flex direction="column" alignItems="center" gap="1rem">
            <TitleText lg>{t('Return Deposit')}</TitleText>
            <DescriptionText>
              {t('Return ${{amount}} to {{resident}}.', {
                amount: refundAmount,
                resident: residentName
              })}
            </DescriptionText>
          </Flex>
          <ModalButtonsContainer gapY="8px" style={{ justifyContent: 'center' }}>
            <ButtonBodyVariant
              cssOverride="width: 8.875rem; height: 2rem;"
              variant="secondary"
              disabled={isRefunding}
              onClick={handleClose}>
              {t('Cancel')}
            </ButtonBodyVariant>
            <ButtonBodyVariant
              cssOverride="width: 8.875rem; height: 2rem;"
              disabled={isRefunding}
              onClick={refundPayment}>
              {t('Confirm')}
            </ButtonBodyVariant>
          </ModalButtonsContainer>
        </Flex>
      </StyledModalBody>
    </PaymentModal>
  );
};

export default RefundPaymentConfirmation;
